/**
 * 分佣总览 - 全平台分佣 + 等级明细
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Share2, DollarSign, TrendingUp, Filter, RefreshCw,
  Users, Award, Activity
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

export function CommissionsView() {
  const [loading, setLoading] = useState(false);
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState('');  // '' / 1 / 2 / 3
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  async function load() {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (filter) params.level = filter;
      const res = await api.admin.commissions(params);
      setCommissions(res.commissions || []);
      setSummary(res.summary || {});
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filter, page]);

  return (
    <div className="space-y-2.5">
      {/* 顶部 4 大统计卡 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard
          icon={DollarSign}
          color="text-accent-success"
          bg="bg-accent-success/10"
          label="分佣总支出"
          value={`¥${(summary.total || 0).toFixed(2)}`}
          sub={`${total} 笔`}
        />
        <StatCard
          icon={Users}
          color="text-brand-cyan"
          bg="bg-brand-cyan/10"
          label="L1 直推"
          value={`¥${(summary.l1_total || 0).toFixed(2)}`}
          sub={`${summary.l1_count || 0} 笔 · 15%`}
        />
        <StatCard
          icon={Award}
          color="text-brand-purple"
          bg="bg-brand-purple/10"
          label="L2 间推"
          value={`¥${(summary.l2_total || 0).toFixed(2)}`}
          sub={`${summary.l2_count || 0} 笔 · 10%`}
        />
        <StatCard
          icon={TrendingUp}
          color="text-brand-gold"
          bg="bg-brand-gold/10"
          label="L3 三推"
          value={`¥${(summary.l3_total || 0).toFixed(2)}`}
          sub={`${summary.l3_count || 0} 笔 · 5%`}
        />
      </div>

      {/* ROI 概览 */}
      <ROICard summary={summary} />

      {/* 筛选 + 列表 */}
      <div className="card !p-2.5 flex items-center gap-2">
        <Share2 size={14} className="text-accent-success" />
        <span className="text-xs font-semibold">分佣明细</span>
        <div className="flex-1" />
        <div className="flex items-center gap-1 bg-bg-base border border-white/5 rounded-md p-0.5">
          {[
            { v: '', label: '全部' },
            { v: '1', label: 'L1' },
            { v: '2', label: 'L2' },
            { v: '3', label: 'L3' },
          ].map(o => (
            <button
              key={o.v}
              onClick={() => { setFilter(o.v); setPage(1); }}
              className={clsx(
                'px-2 py-0.5 rounded text-[10px] transition',
                filter === o.v ? 'bg-white/8 text-white' : 'text-ink-3 hover:bg-white/4'
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
        <button onClick={load} className="btn-ghost text-[10px] flex items-center gap-1 py-0.5 px-1.5">
          <RefreshCw size={10} className={loading ? 'animate-spin' : ''} />
          刷新
        </button>
      </div>

      <div className="card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/2 text-[10px] text-ink-3">
              <tr>
                <th className="text-left py-1.5 px-2.5 font-medium">时间</th>
                <th className="text-left py-1.5 px-2.5 font-medium">来自</th>
                <th className="text-left py-1.5 px-2.5 font-medium">支付</th>
                <th className="text-center py-1.5 px-2.5 font-medium">级</th>
                <th className="text-right py-1.5 px-2.5 font-medium">佣金</th>
                <th className="text-right py-1.5 px-2.5 font-medium">比例</th>
                <th className="text-left py-1.5 px-2.5 font-medium">受益人</th>
                <th className="text-left py-1.5 px-2.5 font-medium">状态</th>
              </tr>
            </thead>
            <tbody>
              {commissions.map(c => (
                <tr key={c.id} className="border-t border-white/3 hover:bg-white/3">
                  <td className="py-1 px-2.5 text-[10px] text-ink-3 font-mono whitespace-nowrap">
                    {new Date(c.created_at).toLocaleString('zh-CN', { hour12: false })}
                  </td>
                  <td className="py-1 px-2.5">
                    <div className="text-[11px]">{c.from_name || '-'}</div>
                    <div className="text-[9px] text-ink-3 font-mono">{c.from_phone}</div>
                  </td>
                  <td className="py-1 px-2.5">
                    <div className="text-[11px]">{c.payment_tier || '-'}</div>
                    <div className="text-[9px] text-ink-3">¥{c.payment_amount}</div>
                  </td>
                  <td className="py-1 px-2.5 text-center">
                    <span className={clsx(
                      'inline-block px-1.5 py-0.5 rounded text-[10px] font-mono',
                      c.level === 1 ? 'bg-brand-cyan/20 text-brand-cyan' :
                      c.level === 2 ? 'bg-brand-purple/20 text-brand-purple' :
                      'bg-brand-gold/20 text-brand-gold'
                    )}>
                      L{c.level}
                    </span>
                  </td>
                  <td className="py-1 px-2.5 text-right font-mono text-[11px] text-accent-success">
                    +¥{c.amount.toFixed(2)}
                  </td>
                  <td className="py-1 px-2.5 text-right text-[10px] text-ink-3">
                    {c.percent}%
                  </td>
                  <td className="py-1 px-2.5">
                    <div className="text-[11px]">{c.to_name || '-'}</div>
                    <div className="text-[9px] text-ink-3 font-mono">{c.to_phone}</div>
                  </td>
                  <td className="py-1 px-2.5">
                    <span className={clsx(
                      'text-[10px] px-1.5 py-0.5 rounded',
                      c.status === 'paid' ? 'bg-accent-success/20 text-accent-success' :
                      'bg-ink-3/20 text-ink-2'
                    )}>
                      {c.status === 'paid' ? '已发' : c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <div className="text-center py-6 text-xs text-ink-3">加载中...</div>}
        {!loading && commissions.length === 0 && (
          <div className="py-12 text-center text-ink-3">
            <Share2 size={32} className="mx-auto opacity-30 mb-2" />
            <div className="text-sm">暂无分佣记录</div>
            <div className="text-[10px] mt-1 opacity-70">用户付费后会自动产生</div>
          </div>
        )}
      </div>

      {/* 分页 */}
      {total > pageSize && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-ghost text-[10px]"
          >
            上一页
          </button>
          <span className="text-[10px] text-ink-3">第 {page} / {Math.ceil(total / pageSize)} 页</span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page * pageSize >= total}
            className="btn-ghost text-[10px]"
          >
            下一页
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, bg, label, value, sub }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="card !p-2.5"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] text-ink-3 mb-1">{label}</div>
          <div className="text-base font-bold font-mono">{value}</div>
          {sub && <div className="text-[10px] text-ink-3 mt-0.5">{sub}</div>}
        </div>
        <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', bg)}>
          <Icon size={14} className={color} />
        </div>
      </div>
    </motion.div>
  );
}

function ROICard({ summary }) {
  const total = summary.total || 0;
  const l1 = summary.l1_total || 0;
  const l2 = summary.l2_total || 0;
  const l3 = summary.l3_total || 0;

  const total_pct = total > 0 ? 100 : 0;
  const l1_pct = total > 0 ? (l1 / total * 100) : 0;
  const l2_pct = total > 0 ? (l2 / total * 100) : 0;
  const l3_pct = total > 0 ? (l3 / total * 100) : 0;

  return (
    <div className="card !p-3">
      <div className="flex items-center gap-2 mb-2">
        <Activity size={12} className="text-brand-cyan" />
        <span className="text-xs font-semibold">分佣结构</span>
        <div className="flex-1" />
        <span className="text-[10px] text-ink-3 font-mono">
          L1 50% · L2 33% · L3 17% (理论)
        </span>
      </div>
      {/* 横向条形图 */}
      <div className="h-2.5 bg-bg-base rounded-full overflow-hidden flex">
        <div style={{ width: `${l1_pct}%` }} className="bg-brand-cyan transition-all" />
        <div style={{ width: `${l2_pct}%` }} className="bg-brand-purple transition-all" />
        <div style={{ width: `${l3_pct}%` }} className="bg-brand-gold transition-all" />
      </div>
      <div className="flex items-center justify-between text-[10px] mt-1.5">
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-brand-cyan" />
          <span className="text-ink-3">L1</span>
          <span className="font-mono">{l1_pct.toFixed(1)}%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-brand-purple" />
          <span className="text-ink-3">L2</span>
          <span className="font-mono">{l2_pct.toFixed(1)}%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold" />
          <span className="text-ink-3">L3</span>
          <span className="font-mono">{l3_pct.toFixed(1)}%</span>
        </div>
        <div className="font-mono font-semibold">{total_pct.toFixed(1)}%</div>
      </div>
    </div>
  );
}
